import { Form } from 'antd';
import PlaylistMedia from '../PlaylistMedia';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'utils/toast';
import { getMessageError } from 'services/response';
import { useEffect, useState } from 'react';
import playlistApis from 'modules/playlist/services/apis';
import ScreenPlaylist from '../ScreenPlaylist';
import { IContentItem } from 'modules/playlist/interfaces/api';
import { Colors } from 'basic-ui-lib';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { IMediaItem } from 'modules/media/interfaces/api';
import MediaDragPreview from './MediaDragPreview';
import getVideoDuration from 'utils/getVideoDuration';

export type FormValues = {
  media?: string;
  link?: string;
  contentItems?: IContentItem[];
  activeZone?: string;
  layoutId?: string;
  name?: string;
};

interface PlaylistFormProps {
  idPlaylist?: string;
}
const PlaylistForm = ({ idPlaylist }: PlaylistFormProps) => {
  const [form] = Form.useForm<FormValues>();
  const location = useLocation();
  const { layoutId, playlistName } = (location.state as { layoutId?: string; playlistName?: string }) || {};
  const navigate = useNavigate();
  const [activeMedia, setActiveMedia] = useState<IMediaItem | null>(null);

  const handleSubmit = async (values: FormValues) => {
    try {
      const res = await playlistApis.createOrUpdatePlaylist({
        id: idPlaylist,
        name: values.name || '',
        duration: values.contentItems?.reduce((sum, item) => sum + (item?.duration || 0), 0) || 0,
        contentItems: values.contentItems || [],
      });
      if (res?.data?.data) {
        toast.success(`Playlist ${idPlaylist ? 'updated' : 'created'} successfully`);
        navigate('/playlist');
      }
    } catch (error) {
      toast.error(getMessageError(error) || `An error occurred while ${idPlaylist ? 'updating' : 'creating'} the playlist`);
    }
  };

  useEffect(() => {
    if (idPlaylist) {
      const fetchPlaylistDetail = async () => {
        try {
          const res = await playlistApis.getDetailPlaylist(idPlaylist);
          if (res?.data?.data) {
            const playlistData = res.data.data;
            form.setFieldsValue({
              name: playlistData.name,
              contentItems: playlistData.contentItems,
            });
          }
        } catch (error) {
          toast.error(getMessageError(error) || 'An error occurred while fetching channel details');
        }
      };

      fetchPlaylistDetail();
    }
  }, [idPlaylist, form]);

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current;
    if (data?.type === 'MEDIA') {
      setActiveMedia(data.item);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveMedia(null);
    if (!over) return;

    if (active.data.current?.type === 'MEDIA' && over.id === 'PLAYLIST_DROP_ZONE') {
      const media = active.data.current.item as IMediaItem;
      const oldItems = form.getFieldValue('contentItems') || [];

      if (oldItems.some((i: IContentItem) => i.id === media.id)) return;

      const nextItem: IContentItem = {
        type: media.type,
        url: media.url || '',
        duration: await getVideoDuration(media.url),
        indexPlay: oldItems.length + 1,
        name: media.name,
        id: media.id,
      };

      form.setFieldsValue({
        contentItems: [...oldItems, nextItem],
      });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );
  return (
    <Container>
      <Form
        form={form}
        layout='vertical'
        className='form-container'
        onFinish={handleSubmit}
        initialValues={{
          layoutId: layoutId,
          name: playlistName,
        }}
      >
        {/* <Form.Item noStyle name='contentItems' hidden>
          <input type='hidden' />
        </Form.Item> */}
        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} sensors={sensors}>
          <LeftSection>
            <ScreenPlaylist form={form} idPlaylist={idPlaylist} />
          </LeftSection>
          <RightSection>
            <PlaylistMedia form={form} />
          </RightSection>
          <DragOverlay
            dropAnimation={null}
          >
            {activeMedia ? <MediaDragPreview item={activeMedia} /> : null}
          </DragOverlay>
        </DndContext>
      </Form>
    </Container>
  );
};

export default PlaylistForm;

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;

  .form-container {
    display: flex;
    width: 100%;
    height: 100%;
  }

  background-color: ${Colors.neutral.bg[2]};
`;

const LeftSection = styled.div`
  flex: 1;
`;
const RightSection = styled.div`
  width: 330px;
  box-shadow: -4px 0 8px 0 rgba(0, 0, 0, 0.15);
  background-color: ${Colors.neutral.bg[1]};
`;
